// What an error report may carry (PQ-08). Sentry gets the stack, the device
// and the account id, never what a coach typed about a player or who they are:
// emails and access tokens in messages, breadcrumbs, URLs and extra data are
// replaced before the report leaves the device.

const EMAIL = /[^\s@"'<>()[\]]+@[^\s@"'<>()[\]]+\.[a-z]{2,}/gi;
const JWT = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g;
const SECRET_PARAM = /([?&#](?:access_token|refresh_token|token|code|apikey|key|password|passwd|pwd)=)[^&#\s]+/gi;
// A password written out ("password: hunter2", "password=...", JSON "password":"..."), e.g. the shared Synergy login's.
const PASSWORD_TEXT = /((?:password|passwd|pwd)["']?\s*[:=]\s*["']?)[^\s"',}&]+/gi;
// The free drop link (#/free-drop/<token>, and the page's /api/free-drop?t=<token>): a key that names an account.
const FREE_DROP = /(free-drop(?:\/|\?t=))[A-Za-z0-9_.%-]+/g;
// The shared Synergy login code on the Film Desk ("Synergy code: 482913"), should it ever reach a message.
const SYNERGY_CODE = /(synergy[\s_-]*(?:login[\s_-]*)?code\W{0,20})\d{4,8}/gi;

export function scrubText(s) {
  if (typeof s !== "string") return s;
  return s.replace(JWT, "[token]").replace(SECRET_PARAM, "$1[redacted]").replace(FREE_DROP, "$1[token]")
    .replace(SYNERGY_CODE, "$1[code]").replace(PASSWORD_TEXT, "$1[redacted]").replace(EMAIL, "[email]");
}

function scrubDeep(v, depth = 0) {
  if (depth > 6) return v;
  if (typeof v === "string") return scrubText(v);
  if (Array.isArray(v)) return v.map(x => scrubDeep(x, depth + 1));
  if (v && typeof v === "object") {
    const out = {};
    Object.entries(v).forEach(([k, x]) => {
      out[k] = /^(email|name|first_name|last_name|full_name|password|passwd|pwd|authorization|cookie|synergy_code|synergy_password|synergy_email|synergy_login|box)$/i.test(k)
        ? "[redacted]" : scrubDeep(x, depth + 1);
    });
    return out;
  }
  return v;
}

/** Sentry beforeSend: keep only the account id on the user; scrub the rest. */
export function scrubSentryEvent(event) {
  if (!event) return event;
  const e = { ...event };
  if (e.user) e.user = e.user.id ? { id: e.user.id } : undefined;
  if (e.message) e.message = scrubText(e.message);
  if (e.exception?.values) {
    e.exception = { ...e.exception, values: e.exception.values.map(v => ({ ...v, value: scrubText(v.value) })) };
  }
  if (e.breadcrumbs) {
    e.breadcrumbs = e.breadcrumbs.map(b => ({ ...b, message: scrubText(b.message), data: scrubDeep(b.data) }));
  }
  if (e.request) {
    const { cookies, headers, data, ...rest } = e.request;
    e.request = { ...rest, url: scrubText(rest.url), query_string: scrubDeep(rest.query_string) };
  }
  if (e.extra) e.extra = scrubDeep(e.extra);
  if (e.contexts) e.contexts = scrubDeep(e.contexts);
  if (e.tags) e.tags = scrubDeep(e.tags);
  return e;
}
